<script lang="ts">
  /* One in-context reminder from `setup-status.hints` (spec 20260909 §2.3):
     shown on the page where the mistake usually happens — an empty requisites
     block when confirming the first order, cards no client can see, an order
     composed but never sent, a supplier that has not opened its catalogue.
     Renders nothing unless the server raised the hint for this company.
     Dismissal is per company + hint (+ order for the unsent-order case). */
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import { push } from "svelte-spa-router";
  import { checklist, hintFor, loadChecklist } from "$lib/onboardingChecklist";

  let { slug, id }: { slug: string; id: "requisites_missing" | "no_client_access" | "unsent_order" | "supplier_not_opened" | "supplier_unavailable" } = $props();

  onMount(() => { void loadChecklist(slug); });

  const hint = $derived($checklist.slug === slug ? hintFor($checklist, id) : undefined);
  const params = $derived((hint?.params ?? {}) as Record<string, string>);
  const dismissKey = $derived(`oe_hint_dismissed_${slug}_${id}_${params.order_code ?? ""}`);
  let dismissedKeys = $state<Set<string>>(new Set());
  $effect(() => {
    try {
      if (localStorage.getItem(dismissKey) === "1") dismissedKeys = new Set([...dismissedKeys, dismissKey]);
    } catch { /* ignore */ }
  });
  const visible = $derived(!!hint && !dismissedKeys.has(dismissKey));

  const href = $derived.by(() => {
    switch (id) {
      case "requisites_missing": return `/t/${slug}/account/document-data`;
      case "no_client_access": return `/t/${slug}/prices?tab=clients`;
      case "unsent_order": return params.order_code ? `/t/${slug}/orders/${params.order_code}` : `/t/${slug}/orders`;
      case "supplier_unavailable": return `/t/${slug}/products?gap=pending`;
      default: return "";
    }
  });
  const icon = $derived(({ requisites_missing: "description", no_client_access: "visibility_off", unsent_order: "send", supplier_not_opened: "hourglass_top", supplier_unavailable: "remove_shopping_cart" } as const)[id]);

  function dismiss() {
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
    dismissedKeys = new Set([...dismissedKeys, dismissKey]);
  }
  function act() {
    if (href) push(href);
    else dismiss();
  }
</script>

{#if visible}
  <div class="setup-hint" role="status">
    <span class="material-icons-outlined hint-icon" aria-hidden="true">{icon}</span>
    <p class="hint-text">{$_(`setup.hint.${id}`, { values: params })}</p>
    <button type="button" class="hint-cta" onclick={act}>{$_(`setup.hint.${id}.cta`)}</button>
    <button type="button" class="hint-close" onclick={dismiss} aria-label={$_("common.close")}>
      <span class="material-icons-outlined">close</span>
    </button>
  </div>
{/if}

<style>
  .setup-hint {
    display: flex; align-items: center; gap: 12px;
    background: #fff8e8; border: 1px solid #f1d9a3; border-left: 4px solid #e0a94a;
    border-radius: var(--radius); padding: 10px 12px 10px 14px;
  }
  .hint-icon { color: #b7791f; font-size: 22px; flex-shrink: 0; }
  .hint-text { margin: 0; flex: 1; min-width: 0; font-size: 13.5px; line-height: 1.45; color: var(--color-surface-darker); }
  .hint-cta {
    flex-shrink: 0; border: 1px solid #b7791f; background: #fff; color: #8a5a12;
    padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
  }
  .hint-cta:hover { background: #fbf1dc; }
  .hint-close { border: none; background: none; cursor: pointer; color: var(--color-text-muted); padding: 4px; border-radius: 4px; display: flex; flex-shrink: 0; }
  .hint-close:hover { background: rgba(0,0,0,0.05); }
  .hint-close .material-icons-outlined { font-size: 18px; }
  @media (max-width: 640px) {
    .setup-hint { flex-wrap: wrap; }
    .hint-cta { margin-left: 34px; }
  }
</style>
